import { useParams } from "react-router-dom";
import WalletDetailView from "@/features/wallets/wallet-detail";
import WalletMissing from "@/features/wallets/wallet-missing";
import { ApiFailure } from "@/components/data-display/api-failure";
import { Skeleton } from "@/components/ui/feedback";
import { buttonVariants } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useWalletDetail } from "@/hooks/use-wallet-detail";
import { useWalletActivity } from "@/hooks/use-wallet-activity";
import { useWalletRecentTransfers } from "@/hooks/use-wallet-recent-transfers";

export default function WalletDetailPage() {
  const { id } = useParams();
  // Route changes must discard the previous wallet's detail, activity, and transfer state
  // before the next wallet can render. Unmounting also prevents late responses from the
  // previous wallet from replacing the new page's data.
  return <WalletDetailContent key={id} id={id!} />;
}

function WalletDetailContent({ id }: { id: string }) {
  const { detail, loading, failure, notFound, refresh, refreshSilently } = useWalletDetail(id);
  // ALL-5458: incoming funds poll while the page is open; a new or newly confirmed transfer
  // also refreshes the balances it moved.
  const { activity, loading: activityLoading, refresh: refreshActivity } = useWalletActivity(id, refreshSilently);
  const { transfers, loading: transfersLoading, error: transfersError, refresh: refreshTransfers } = useWalletRecentTransfers(id);

  if (loading) {
    return (
      <div aria-label="Loading wallet" role="status" className="space-y-4">
        <Skeleton shimmer className="h-8 w-64" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Skeleton shimmer className="h-20" />
          <Skeleton shimmer className="h-20" />
          <Skeleton shimmer className="h-20" />
        </div>
        <Skeleton shimmer className="h-48" />
      </div>
    );
  }

  if (!detail) {
    // Only a wallet that is known to be absent is "not found"; a denial or a failed read
    // says what it is, never "not found" (ALL-5736).
    if (notFound || !failure) return <WalletMissing id={id} />;
    return (
      <ApiFailure
        error={failure}
        subject="this wallet"
        onRetry={refresh}
        action={
          <Link to="/wallets" className={buttonVariants({ variant: "secondary" })}>
            Back to wallets
          </Link>
        }
      />
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            refresh();
            refreshActivity();
            refreshTransfers();
          }}
          className="text-xs text-muted underline hover:text-ink"
        >
          Refresh
        </button>
      </div>
      <WalletDetailView
        detail={detail}
        activity={activity}
        activityLoading={activityLoading}
        recentTransfers={transfers}
        recentTransfersLoading={transfersLoading}
        recentTransfersErrorCode={transfersError?.reasonCode ?? null}
      />
    </div>
  );
}
